import React, { useState, useEffect } from 'react';
import { Container, Alert, Table, Spinner, Button, Badge, Modal, Form, FloatingLabel, Accordion, Row, Col } from 'react-bootstrap';
import { FaEdit } from 'react-icons/fa';
import UserService from "../services/user.service";

const S7MyPaymentMethods = () => {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    
    // Initialer State für das Formular, inklusive des verschachtelten 'configuration'-Objekts
    const initialFormData = {
        method_name: '',
        provider_key: '',
        is_active: true,
        configuration: {
            publishableKey: '',
            secretKey: '',
            webhookSecret: '',
            successUrl: '', // NEUES FELD für die Weiterleitungs-URL
            customerConfirmationRequired: false,
            customerCompletesPayment: false,
            emailTemplateId: '',
            paymentDays: 14 
        }
    };

    const [formData, setFormData] = useState(initialFormData);
    const [editingMethod, setEditingMethod] = useState(null);
    const [emailTemplates, setEmailTemplates] = useState([]); // State für E-Mail-Vorlagen

    const modalTitle = editingMethod ? "Zahlungsmethode bearbeiten" : "Neue Zahlungsmethode anlegen";

    // Lädt E-Mail-Vorlagen vom Server
    const fetchEmailTemplates = () => {
        // HINWEIS: Ersetzen Sie dies durch Ihren tatsächlichen API-Aufruf, sobald er existiert.
        // z.B. UserService.getEmailTemplates().then(response => setEmailTemplates(response.data));
        
        // Platzhalterdaten, bis der Endpunkt existiert
        setEmailTemplates([
            { id: 1, name: 'Standard-Zahlungsaufforderung via E-Mail' },
            { id: 2, name: 'Erinnerung: Zahlung für Ihr Abonnement' }
        ]);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingMethod(null);
        setFormData(initialFormData); // Formular komplett zurücksetzen
    };

    const handleShowCreateModal = () => {
        setEditingMethod(null);
        setFormData(initialFormData);
        setShowModal(true);
    };

    // Bearbeiten-Modal holt sich jetzt die kompletten Daten
    const handleShowEditModal = (method) => {
        setLoading(true);
        setError('');
        UserService.getPaymentMethodById(method.payment_method_id)
            .then(response => {
                const fullMethod = response.data;
                setEditingMethod(fullMethod);
                
                const config = fullMethod.configuration || {};
                
                setFormData({
                    method_name: fullMethod.method_name,
                    provider_key: fullMethod.provider_key,
                    is_active: fullMethod.is_active,
                    configuration: {
                        ...initialFormData.configuration,
                        ...config
                    }
                });
                setShowModal(true);
            })
            .catch(() => setError("Fehler beim Laden der Methodendetails."))
            .finally(() => setLoading(false));
    };
    
    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        const formValue = type === 'checkbox' ? checked : value;
    
        if (Object.keys(formData.configuration).includes(name)) {
            setFormData(prev => ({
                ...prev,
                configuration: { ...prev.configuration, [name]: formValue }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: formValue }));
        }
    };

    const handleSaveMethod = () => {
        if (!formData.method_name || !formData.provider_key) {
            alert("Bitte füllen Sie Name und Technischen Schlüssel aus.");
            return;
        }

        const promise = editingMethod
            ? UserService.updatePaymentMethod(editingMethod.payment_method_id, formData)
            : UserService.createPaymentMethod(formData);

        promise.then(() => {
            handleCloseModal();
            fetchMethods();
        }).catch(error => {
            setError(error.response?.data?.message || "Ein Fehler ist aufgetreten.");
        });
    };

    const handleToggleStatus = (method) => {
        const newStatus = !method.is_active;
        const actionText = newStatus ? "aktivieren" : "deaktivieren";
        if (window.confirm(`Möchten Sie die Methode "${method.method_name}" wirklich ${actionText}?`)) {
            UserService.updatePaymentMethodStatus(method.payment_method_id, { is_active: newStatus })
                .then(fetchMethods)
                .catch(error => setError(error.response?.data?.message || `Fehler beim Ändern des Status.`));
        }
    };

    const fetchMethods = () => {
        setError('');
        setLoading(true);
        UserService.getPaymentMethods()
            .then(response => {
                setPaymentMethods(Array.isArray(response.data) ? response.data : []);
            })
            .catch(() => setError("Fehler beim Laden der Zahlungsarten."))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchMethods();
        fetchEmailTemplates();
    }, []);
    
    return (
        <Container>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1>Meine Zahlungsarten</h1>
                <Button variant="primary" onClick={handleShowCreateModal}>+ Neue Methode</Button>
            </div>
            {loading && <div className="text-center"><Spinner /></div>}
            {error && <Alert variant="danger">{error}</Alert>}
            {!loading && !error && (
                 <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Schlüssel</th>
                            <th>Status</th>
                            <th>Aktionen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(paymentMethods) && paymentMethods.length > 0 ? paymentMethods.map((method) => (
                            <tr key={method.payment_method_id}>
                                <td>{method.method_name}</td>
                                <td><code>{method.provider_key}</code></td>
                                <td><Badge bg={method.is_active ? 'success' : 'secondary'}>{method.is_active ? 'Aktiv' : 'Inaktiv'}</Badge></td>
                                <td>
                                    <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleShowEditModal(method)} title="Bearbeiten" aria-label="Bearbeiten"><FaEdit /></Button>
                                    <Button variant={method.is_active ? "outline-danger" : "outline-success"} size="sm" onClick={() => handleToggleStatus(method)}>
                                        {method.is_active ? 'Deaktivieren' : 'Aktivieren'}
                                    </Button>
                                </td>
                            </tr>
                        )) : <tr><td colSpan="4" className="text-center">Keine Zahlungsarten gefunden.</td></tr>}
                    </tbody>
                </Table>
            )}

            <Modal show={showModal} onHide={handleCloseModal} size="lg" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Accordion defaultActiveKey={['0', '1']} alwaysOpen>
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>Basisinformationen</Accordion.Header>
                                <Accordion.Body>
                                    <Row>
                                        <Col md={6}>
                                            <FloatingLabel label="Name (z.B. 'Stripe', 'Überweisung')" className="mb-3">
                                                <Form.Control type="text" name="method_name" value={formData.method_name} onChange={handleFormChange} required />
                                            </FloatingLabel>
                                        </Col>
                                        <Col md={6}>
                                            <FloatingLabel label="Techn. Schlüssel (z.B. 'STRIPE')" className="mb-3">
                                                <Form.Control type="text" name="provider_key" value={formData.provider_key} onChange={handleFormChange} required />
                                            </FloatingLabel>
                                        </Col>
                                    </Row>
                                    <Form.Check type="switch" label="Diese Methode ist aktiv" name="is_active" checked={formData.is_active} onChange={handleFormChange} />
                                </Accordion.Body>
                            </Accordion.Item>

                            <Accordion.Item eventKey="1">
                                <Accordion.Header>Allgemeine Konfiguration</Accordion.Header>
                                <Accordion.Body>
                                    <Form.Group className="mb-3">
                                        <Form.Check type="switch" label="Kundenbestätigung erforderlich" name="customerConfirmationRequired" checked={formData.configuration.customerConfirmationRequired} onChange={handleFormChange} />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Check type="switch" label="Zahlung schließt Kunde eigenständig ab" name="customerCompletesPayment" checked={formData.configuration.customerCompletesPayment} onChange={handleFormChange} />
                                    </Form.Group>
                                    
                                    {formData.configuration.customerCompletesPayment && (
                                        <FloatingLabel label="E-Mail-Vorlage für Zahlungslink" className="mb-3">
                                            <Form.Select name="emailTemplateId" value={formData.configuration.emailTemplateId} onChange={handleFormChange}>
                                                <option value="">Vorlage auswählen...</option>
                                                {emailTemplates.map(template => (
                                                    <option key={template.id} value={template.id}>{template.name}</option>
                                                ))}
                                            </Form.Select>
                                        </FloatingLabel>
                                    )}

                                    {formData.provider_key === 'BANK_TRANSFER' && (
                                        <FloatingLabel label="Zahlungsziel in Tagen" className="mb-3">
                                            <Form.Control type="number" name="paymentDays" value={formData.configuration.paymentDays} onChange={handleFormChange} />
                                        </FloatingLabel>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>
                            
                            {['STRIPE', 'PAY_LINK', 'TAP_TO_PAY'].includes(formData.provider_key) && (
                                <Accordion.Item eventKey="2">
                                    <Accordion.Header>Stripe-Konfiguration</Accordion.Header>
                                    <Accordion.Body>
                                        <Alert variant="warning"><strong>Achtung:</strong> Behandeln Sie den Secret Key und Webhook Secret wie ein Passwort.</Alert>
                                        <FloatingLabel label="Publishable Key (Veröffentlichbarer Schlüssel)" className="mb-3">
                                            <Form.Control type="text" name="publishableKey" value={formData.configuration.publishableKey} onChange={handleFormChange} />
                                        </FloatingLabel>
                                        <FloatingLabel label="Secret Key (Geheimer Schlüssel)" className="mb-3">
                                            <Form.Control type="password" name="secretKey" value={formData.configuration.secretKey} onChange={handleFormChange} />
                                        </FloatingLabel>
                                        <FloatingLabel label="Webhook Signing Secret" className="mb-3">
                                            <Form.Control type="password" name="webhookSecret" value={formData.configuration.webhookSecret} onChange={handleFormChange} />
                                        </FloatingLabel>
                                        <hr />
                                        <h5 className="mt-4">Weiterleitungs-Einstellungen</h5>
                                        <FloatingLabel label="URL für erfolgreiche Zahlung" className="mb-3">
                                            <Form.Control type="text" name="successUrl" value={formData.configuration.successUrl} onChange={handleFormChange} placeholder="https://www.myn.club/danke" />
                                        </FloatingLabel>
                                        <Form.Text muted>
                                            Tragen Sie https://www.myn.club/danke ein. Dorthin kommt der Kunde nach der Zahlung.
                                        </Form.Text>
                                    </Accordion.Body>
                                </Accordion.Item>
                            )}
                        </Accordion>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>Abbrechen</Button>
                    <Button variant="primary" onClick={handleSaveMethod}>Speichern</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default S7MyPaymentMethods;